import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Paper,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Link,
    useTheme,
    alpha
} from '@mui/material';
import { authService } from '../../services/authService';
import { extractApiErrorMessage } from '../../utils/apiUtils';
import { DEFAULT_THEME } from '../../constants/themeDefaults';
import LoginLayout from '../../components/layout/LoginLayout';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export const VerifyEmail: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [isValidToken, setIsValidToken] = useState(true);
    const [isVerified, setIsVerified] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = DEFAULT_THEME;

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    useEffect(() => {
        if (!token || !email) {
            setIsValidToken(false);
            setMessage({ type: 'error', text: 'Invalid or missing verification link.' });
            return;
        }

        // Auto-verify if we have the token and email
        handleVerify();
    }, [token, email]);

    const handleVerify = async () => {
        if (!token || !email) return;

        setIsLoading(true);
        setMessage(null);

        try {
            // For now, we'll need to get the userId from the email
            // In a real implementation, you might want to store the userId in the token or email
            // For this example, we'll use the email as a fallback
            const result = await authService.verifyEmail(email, token);
            
            if (result.success) {
                setMessage({ type: 'success', text: 'Email verified successfully! You can now log in.' });
                setIsVerified(true);
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: extractApiErrorMessage(error, 'An unexpected error occurred. Please try again.') });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendVerification = async () => {
        if (!email) return;

        setIsLoading(true);
        setMessage(null);

        try {
            const result = await authService.sendEmailVerification(email);
            
            if (result.success) {
                setMessage({ type: 'success', text: 'Verification email sent again! Check your inbox.' });
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: extractApiErrorMessage(error, 'An unexpected error occurred. Please try again.') });
        } finally {
            setIsLoading(false);
        }
    };

    if (!isValidToken) {
        return (
            <LoginLayout>
                <Container component="main" maxWidth="sm">
                    <Box
                        sx={{
                            marginTop: 8,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                padding: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                backgroundColor: alpha(theme.backgroundColor, 0.9),
                                backdropFilter: 'blur(10px)',
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.primaryColor, 0.2)}`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <CalendarMonthIcon
                                    sx={{
                                        fontSize: 40,
                                        color: theme.primaryColor,
                                        mr: 1,
                                    }}
                                />
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={{
                                        color: theme.textColor,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    MindShare
                                </Typography>
                            </Box>

                            <Typography
                                component="h2"
                                variant="h5"
                                sx={{
                                    color: theme.textColor,
                                    mb: 3,
                                    textAlign: 'center',
                                }}
                            >
                                Invalid Verification Link
                            </Typography>

                            {message && (
                                <Alert
                                    severity={message.type}
                                    sx={{ mb: 2, width: '100%', whiteSpace: 'pre-line' }}
                                >
                                    {message.text}
                                </Alert>
                            )}

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Link
                                    component={RouterLink}
                                    to="/login"
                                    variant="body2"
                                    sx={{
                                        color: theme.primaryColor,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    Back to Login
                                </Link>
                            </Box>
                        </Paper>
                    </Box>
                </Container>
            </LoginLayout>
        );
    }

    return (
        <LoginLayout>
            <Container component="main" maxWidth="sm">
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: alpha(theme.backgroundColor, 0.9),
                            backdropFilter: 'blur(10px)',
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.primaryColor, 0.2)}`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <CalendarMonthIcon
                                sx={{
                                    fontSize: 40,
                                    color: theme.primaryColor,
                                    mr: 1,
                                }}
                            />
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{
                                    color: theme.textColor,
                                    fontWeight: 'bold',
                                }}
                            >
                                MindShare
                            </Typography>
                        </Box>

                        {isVerified ? (
                            <>
                                <CheckCircleIcon
                                    sx={{
                                        fontSize: 64,
                                        color: theme.primaryColor,
                                        mb: 2,
                                    }}
                                />

                                <Typography
                                    component="h2"
                                    variant="h5"
                                    sx={{
                                        color: theme.textColor,
                                        mb: 3,
                                        textAlign: 'center',
                                    }}
                                >
                                    Email Verified!
                                </Typography>

                                {message && (
                                    <Alert
                                        severity={message.type}
                                        sx={{ mb: 2, width: '100%', whiteSpace: 'pre-line' }}
                                    >
                                        {message.text}
                                    </Alert>
                                )}

                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <Link
                                        component={RouterLink}
                                        to="/login"
                                        variant="body2"
                                        sx={{
                                            color: theme.primaryColor,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        Continue to Login
                                    </Link>
                                </Box>
                            </>
                        ) : (
                            <>
                                <EmailOutlinedIcon
                                    sx={{
                                        fontSize: 64,
                                        color: theme.primaryColor,
                                        mb: 2,
                                    }}
                                />

                                <Typography
                                    component="h2"
                                    variant="h5"
                                    sx={{
                                        color: theme.textColor,
                                        mb: 3,
                                        textAlign: 'center',
                                    }}
                                >
                                    {isLoading ? 'Verifying Email...' : 'Verify Your Email'}
                                </Typography>

                                {message && (
                                    <Alert
                                        severity={message.type}
                                        sx={{ mb: 2, width: '100%', whiteSpace: 'pre-line' }}
                                    >
                                        {message.text}
                                    </Alert>
                                )}

                                {!isLoading && !message && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.textColor,
                                            mb: 3,
                                            textAlign: 'center',
                                            opacity: 0.8,
                                        }}
                                    >
                                        Click the button below to verify your email address.
                                    </Typography>
                                )}

                                {!isLoading && !message && (
                                    <Button
                                        onClick={handleVerify}
                                        variant="contained"
                                        sx={{
                                            mb: 2,
                                            backgroundColor: theme.primaryColor,
                                            '&:hover': {
                                                backgroundColor: alpha(theme.primaryColor, 0.8),
                                            },
                                        }}
                                    >
                                        Verify Email
                                    </Button>
                                )}

                                {isLoading && (
                                    <CircularProgress
                                        size={48}
                                        sx={{ color: theme.primaryColor, mb: 2 }}
                                    />
                                )}

                                {!isLoading && message && message.type === 'error' && (
                                    <Button
                                        onClick={handleResendVerification}
                                        variant="outlined"
                                        sx={{
                                            mb: 2,
                                            borderColor: theme.primaryColor,
                                            color: theme.primaryColor,
                                            '&:hover': {
                                                borderColor: theme.primaryColor,
                                                backgroundColor: alpha(theme.primaryColor, 0.1),
                                            },
                                        }}
                                    >
                                        Resend Verification Email
                                    </Button>
                                )}

                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <Link
                                        component={RouterLink}
                                        to="/login"
                                        variant="body2"
                                        sx={{
                                            color: theme.primaryColor,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        Back to Login
                                    </Link>
                                </Box>
                            </>
                        )}
                    </Paper>
                </Box>
            </Container>
        </LoginLayout>
    );
};

export default VerifyEmail;
